import { addYears, format, isBefore, parseISO, startOfMonth, subDays } from "date-fns";
import { PeriodPresetKeys } from "../types/orderAnalytics.types";
import type {
  AnalyticsPeriod,
  DateString,
  PeriodPresetKey,
} from "../types/orderAnalytics.types";

export const DATE_FORMAT = "yyyy-MM-dd";

export const toDateString = (date: Date): DateString => format(date, DATE_FORMAT);

export const PERIOD_PRESETS: { key: PeriodPresetKey; label: string }[] = [
  { key: PeriodPresetKeys.YESTERDAY, label: "어제" },
  { key: PeriodPresetKeys.LAST_7_DAYS, label: "최근 7일" },
  { key: PeriodPresetKeys.LAST_30_DAYS, label: "최근 30일" },
  { key: PeriodPresetKeys.THIS_MONTH, label: "이번 달" },
];

export const DEFAULT_PRESET: PeriodPresetKey = PeriodPresetKeys.LAST_7_DAYS;

/** "최근 N일"은 오늘을 포함한다. "이번 달"은 1일부터 오늘까지. */
export const getPresetPeriod = (
  key: PeriodPresetKey,
  today: Date = new Date(),
): AnalyticsPeriod => {
  switch (key) {
    case PeriodPresetKeys.YESTERDAY: {
      const yesterday = toDateString(subDays(today, 1));
      return { startDate: yesterday, endDate: yesterday };
    }
    case PeriodPresetKeys.LAST_7_DAYS:
      return {
        startDate: toDateString(subDays(today, 6)),
        endDate: toDateString(today),
      };
    case PeriodPresetKeys.LAST_30_DAYS:
      return {
        startDate: toDateString(subDays(today, 29)),
        endDate: toDateString(today),
      };
    case PeriodPresetKeys.THIS_MONTH:
      return {
        startDate: toDateString(startOfMonth(today)),
        endDate: toDateString(today),
      };
  }
};

/** 직접 고친 날짜가 어떤 프리셋과 일치하는지. 없으면 null (강조 해제). */
export const findMatchingPreset = (
  period: AnalyticsPeriod,
  today: Date = new Date(),
): PeriodPresetKey | null =>
  PERIOD_PRESETS.find(({ key }) => {
    const preset = getPresetPeriod(key, today);
    return (
      preset.startDate === period.startDate && preset.endDate === period.endDate
    );
  })?.key ?? null;

/** 조회를 막아야 하면 안내 문구, 정상이면 null. 서버(StatisticsService)도 같은 규칙으로 막는다. */
export const validatePeriod = (period: AnalyticsPeriod): string | null => {
  const start = parseISO(period.startDate);
  const end = parseISO(period.endDate);
  if (isBefore(end, start)) return "시작일이 종료일보다 늦습니다.";
  if (!isBefore(end, addYears(start, 1)))
    return "조회 기간은 최대 1년입니다.";
  return null;
};
